import { DeliveryProviderPortalService } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal';

import { Gateway, RequestError } from '@bolteu/react-api-gateway/es';
import { JwtAuth } from '@bolteu/react-api-gateway/es/src/auth/JwtAuth';
import objectToQueryString from '@bolteu/react-api-gateway/es/src/objectToQueryString';

import { TIMEOUT_60000_MS } from '../constants';
import {
  BulkDeleteMenuItemsRequest,
  BulkUpdateMenuEntityRequest,
  CreateMenuEntityRequest,
  CreateMenuEntityResponse,
  DeleteChildEntityRequest,
  DeleteMenuItemRequest,
  GetAvailableTagsForClientRequest,
  GetAvailableTagsForClientResponse,
  GetChildEntitiesRequest,
  GetChildEntitiesResponse,
  GetMenuEditorFeatureFlagsResponse,
  GetMenuEntitiesRequest,
  GetMenuEntitiesResponse,
  GetMenuEntitiesWithAgeRestrictionsResponse,
  GetMenuEntityRequest,
  GetMenuEntityResponse,
  GetMenuPreviewRequest,
  GetMenuPreviewResponse,
  MenuEntityImageRequest,
  MenuEntityImageResponse,
  MenuItemImagesResponse,
  MenuItemsChildrenCountRequest,
  MenuItemsChildrenCountResponse,
  UpdateMenuEntityRequest,
  UpdateMenuEntityResponse,
  UpdateMenuItemsDisplayOrderRequest,
} from '../menuEditor/gen/MenuItemsOperations';
import {
  BulkUploadMenuItemImagesFromCSVRequest,
  BulkUploadMenuItemImagesFromCSVResponse,
  DownloadProviderDraftMenusCSVRequest,
  DownloadProviderDraftMenusCSVResponse,
  EmptyMenuRequest,
  EmptyMenuResponse,
  GetMenuInfoRequest,
  GetMenuInfoResponse,
  GetProviderMenusRequest,
  HasAccessToMenuEditorResponse,
  IsMenuBulkUploadEnabledForProviderPortalAccountResponse,
} from '../menuEditor/gen/MenusOperations';
import { TranslatableField } from '../types';
import ProviderLifecycleStatus = DeliveryProviderPortalService.ProviderLifecycleStatus;
import ProviderSignupSource = DeliveryProviderPortalService.ProviderSignupSource;

export interface MenuManagementCsvUpdateRequest {
  csv_data: string;
  provider_id: number;
}

export interface CSVImportResponse {
  items_updated: boolean;
  items_not_found: string[];
}

export interface ProviderMenuRequest {
  provider_id: number;
  language: string;
}

export interface CampaignTagsConfigResponse {
  tags_config: {
    tag_prefixes: string[];
  };
}

export interface PreselectedOptionsEnablementConfigResponse {
  provider_ids: number[];
  enabled_by_default: boolean;
}

export interface ProviderMenuResponse {
  id: number;
  type: MenuItemType.MENU;
  categories: ProviderMenuItemCategory[];
}
export enum MenuItemType {
  MENU = 'menu',
  DISH = 'dish',
  CATEGORY = 'category',
  OPTION_SELECT = 'option_select',
  OPTION_SELECT_GROUP = 'option_select_group',
  OPTION_MULTI_SELECT = 'option_multi_select',
  OPTION_MULTI_SELECT_GROUP = 'option_multi_select_group',
  OPTION_COUNTER = 'option_counter',
  OPTION_COUNTER_GROUP = 'option_counter_group',
}

export interface ProviderMenuItemCategory extends ProviderBaseMenuItem {
  type: MenuItemType.CATEGORY;
  dishes: ProviderMenuItemDish[];
}
export interface ProviderImages {
  menu_item_list_v1: ProviderImagesListV1;
}

export interface ProviderImagesListV1 {
  aspect_ratio_map: ProviderImagesAspectRatio;
  meta: ProviderImagesMeta;
}

export interface ProviderImagesMeta {
  mime_type: string;
  size: number;
  origina_id: number;
}

export interface ProviderImagesAspectRatio {
  original: ProviderImagesAspectRatioImages;
}

export interface ProviderImagesAspectRatioImages {
  '1x': string;
  '2x': string;
  '3x': string;
}

export interface Price {
  value: number;
  currency: string;
  price_str: string;
}
export interface PriceBreakdown {
  delivery_fee: Price;
  service_fee: Price;
  final_price: Price;
  small_order_fee: Price;
}
export interface ProviderBaseMenuItem {
  id: number;
  type: MenuItemType;
  name: LocalisedString | null;
  description: LocalisedString | null;
  price: Price | null;
  images: ProviderImages;
}
export interface LocalisedString {
  locale: string;
  value: string;
}
export interface ProviderMenuItemDish extends ProviderBaseMenuItem {
  option_groups: ProviderMenuItemOptionGroupBase[];
  availability: MenuItemAvailabilityStatusWithScheduledChanges;
}
export interface ProviderMenuItemOptionGroupBase extends ProviderBaseMenuItem {
  type:
    | MenuItemType.OPTION_SELECT_GROUP
    | MenuItemType.OPTION_MULTI_SELECT_GROUP
    | MenuItemType.OPTION_COUNTER_GROUP;
  options: ProviderMenuItemOptionBase[];
}
export interface ProviderMenuItemOptionBase extends ProviderBaseMenuItem {
  type: MenuItemType.OPTION_SELECT | MenuItemType.OPTION_MULTI_SELECT | MenuItemType.OPTION_COUNTER;
  availability: MenuItemAvailabilityStatusWithScheduledChanges;
}
export interface MenuItemAvailabilityStatusWithScheduledChanges {
  status: MenuItemAvailabilityStatus;
  scheduled_status_changes: MenuItemAvailabilityStatusScheduledChange[];
}
export enum MenuItemAvailabilityStatus {
  DELETED = 'deleted',
  RELEASED = 'released',
  IN_STOCK = 'in_stock',
  OUT_OF_STOCK = 'out_of_stock',
  HIDDEN = 'hidden',
}
export interface MenuItemAvailabilityStatusScheduledChange {
  to_status: MenuItemAvailabilityStatus;
  scheduled_timestamp: number;
}
export interface ProviderUpdateMenuItemAvailabilityRequest {
  menu_item_id: number;
  to_status: MenuItemAvailabilityStatus.IN_STOCK | MenuItemAvailabilityStatus.OUT_OF_STOCK;
  /**
   * This is to support out of stock until tomorrow/end of business day. Server will compute when the transition will
   * happen and schedule an event.
   */
  should_schedule_in_stock_transition?: boolean;
}
export interface ProviderUpdateMenuItemAvailabilityResponse {
  updated_menu_item_ids: number[];
}

export interface MenuItemsBulkUpdateRequest {
  language: string;
  provider_id: number;
  menu_items: MenuItemUpdateData[];
}
export interface MenuItemUpdateData {
  menu_item_id: number;
  price?: MenuItemPriceUpdateData;
  item_availability?: MenuItemAvailabilityUpdateData;
}
export interface MenuItemAvailabilityUpdateData {
  to_status: AllowedMenuItemAvailabilitiesForUpdate;
  should_schedule_in_stock_transition?: boolean;
}
export interface MenuItemPriceUpdateData {
  value: number;
}
export type AllowedMenuItemAvailabilitiesForUpdate =
  | MenuItemAvailabilityStatus.IN_STOCK
  | MenuItemAvailabilityStatus.OUT_OF_STOCK
  | MenuItemAvailabilityStatus.HIDDEN;

export interface GetProviderScheduleRequest {
  provider_id: number;
}
export interface GetProviderScheduleResponse {
  schedule: ScheduleV0 | null;
}
export interface ScheduleV0 {
  [day: number]: Interval[];
}
export interface Interval {
  start_at_minutes_from_midnight: number;
  stop_at_minutes_from_midnight: number;
}
export interface UpdateProviderScheduleRequest {
  provider_id: number;
  schedule: ScheduleV0;
}

export interface ProviderPortalDownloadProviderReportsFilter extends ReportsFilter {}

export interface DownloadProvidersReportRequest {
  provider_ids: number[];
  start_interval_timestamp_seconds: number;
  end_interval_timestamp_seconds: number;
  type?: ExportFileTypes;
  download_provider_report_filter?: ProviderPortalDownloadProviderReportsFilter;
}

export interface GetProviderWeeklyReportCsvRequest {
  report_id: number;
}

export interface WeeklyReportListEntry {
  report_id: number | null;
  week_start_unix_ts: number;
  week_end_unix_ts: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface GetProviderWeeklyReportListRequest {}

export interface GetProviderWeeklyReportListResponse {
  reports: WeeklyReportListEntry[];
}

export interface GetProviderWeeklyReportRequest {
  report_id: number;
  provider_id?: number;
  vendor_id?: number;
}

export interface GetProviderWeeklyReportResponsePayload {
  latest_week_metrics: LatestMetricsWithTrend;
  chart_metrics: ChartMetrics;
}

export interface GetProviderWeeklyReportResponse {
  weekly_report: GetProviderWeeklyReportResponsePayload | null;
}

export interface LatestMetricsWithTrend {
  total_orders: WeeklyReportMetricWithTrend;
  completed_orders: WeeklyReportMetricWithTrend;
  rejected_orders: WeeklyReportMetricWithTrend;
  revenue: WeeklyReportMetricWithTrend;
  average_rating: WeeklyReportMetricWithTrend;
  availability: WeeklyReportMetricWithTrend;
  acceptance_rate: WeeklyReportMetricWithTrend;
  cooked_on_time: WeeklyReportMetricWithTrend;
  average_cooking_time: WeeklyReportMetricWithTrend;
  courier_waiting_time: WeeklyReportMetricWithTrend;
  average_order_value: WeeklyReportMetricWithTrend;
  total_sales_lost: WeeklyReportMetricWithTrend;
  unique_users: WeeklyReportMetricWithTrend;
  conversion: WeeklyReportMetricWithTrend;
}

export interface ChartMetrics {
  total_orders: ChartMetric;
  revenue: ChartMetric;
  average_rating: ChartMetric;
  availability: ChartMetric;
  average_order_value: ChartMetric;
  total_sales_lost: ChartMetric;
  unique_users: ChartMetric;
  conversion: ChartMetric;
}

export interface WeeklyReportMetricWithTrend {
  type: WeeklyReportMetricType;
  value: number | null;
  unit?: string;
  wow_diff_percentage?: number;
}

export enum WeeklyReportMetricType {
  PERCENT = 'percent',
  COUNT = 'count',
  AVERAGE = 'average',
  TIME = 'time',
}

export enum ChartType {
  LINE = 'line',
  BAR = 'bar',
  STACKED_BAR = 'stacked_bar',
}

export type ChartMetric = ChartMetricLine | ChartMetricBar | ChartMetricStacked;

export interface ChartMetricBase {
  type: ChartType;
  is_percentage?: boolean;
}

export interface ChartMetricLine extends ChartMetricBase {
  type: ChartType.LINE;
  data: ChartMetricDataSingleValue;
}

export interface ChartMetricBar extends ChartMetricBase {
  type: ChartType.BAR;
  data: ChartMetricDataSingleValue;
}

export interface ChartMetricStacked extends ChartMetricBase {
  type: ChartType.STACKED_BAR;
  data: ChartMetricDataMultipleValues;
}

export interface ChartMetricDataSingleValue {
  [label: string]: number | null;
}

export interface ChartMetricDataTuple {
  [key: string]: number | null;
}

export interface ChartMetricDataMultipleValues {
  [label: string]: ChartMetricDataTuple[] | null;
}

export interface GetProviderWeeklyReportsListRequest {
  provider_id: number;
}

export interface GetProviderWeeklyReportsListResponse {
  reports: WeeklyReportForProvider[];
}

export interface WeeklyReportForProvider {
  report_id: number;
  week_start_unix_ts: number;
  week_end_unix_ts: number;
}

export enum ExportFileTypes {
  CSV = 'csv',
  XLS = 'xls',
}

export interface FileResponse {
  file_name: string;
  file_content: string;
  file_type: string;
}

export interface GetProviderStatisticsRequest extends GetProviderStatisticsFilters {
  provider_id: number;
}

export interface GetProviderStatisticsFilters {
  ratings_with_comments_only?: boolean;
  provider_order_states?: ProviderOrderState[];
}

export interface GetProviderStatisticsResponse {
  current_interval: CurrentIntervalStatistics;
  previous_interval: PreviousIntervalStatistics;
  ratings: ProviderOrderRating[];
}
export interface ProviderOrderRating {
  created_timestamp_seconds: number | null;
  rating_value: number;
  comment: string | null;
  order_reference_id: string;
  highlights: ProviderOrderRatingCompactHighlight[];
}

export interface ProviderOrderRatingCompactHighlight {
  key: string;
  localisedText: string;
}

export interface ProviderOrderRatingRawHighlight {
  id: number;
  key: string;
  text: {
    locale: string;
    value: string;
  };
}

export interface IntervalStatistics {
  order_no: number;
  order_total: Money;
  interval_local_start_timestamp_seconds: number;
  interval_local_end_timestamp_seconds: number;
  avg_rating: number | null;
  rating_no: number;
}
export interface PreviousIntervalStatistics extends IntervalStatistics {
  spendOnPromotions: { value: number; currency: string };
}
export interface CurrentIntervalStatistics extends IntervalStatistics {
  order_number_variation: number;
  order_total_variation: number;
  spendOnPromotions: { value: number; currency: string };
  spendOnPromotionsVariation: number;
}
export interface Money {
  value: number;
  currency: string;
}

export interface GetOrderHistoryPagesRequest extends OrderHistoryPagesQuery {
  provider_id: number;
}
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface GetLatestOnboardingProviderForAccountRequest {}

export interface GetLatestOnboardingProviderForAccountResponse {
  id: number;
  lifecycle_status: ProviderLifecycleStatus;
  signup_source: ProviderSignupSource;
}
export interface GetDisputesPaginatedData {
  provider_id: number;
  requested_start_timestamp: number;
  requested_end_timestamp: number;
  page?: number;
  items_per_page: number;
}
export interface DbPaginatedQueryPages {
  has_previous_page: boolean;
  has_next_page: boolean;
  previous_page: number;
  next_page: number;
  items_per_page: number;
  current_page: number;
  total_pages: number;
  total_rows: number;
}
export interface DbPaginatedQueryResponse<T> {
  list: T[];
  pages: DbPaginatedQueryPages;
}
export type EntityId<Name extends string> = number & {
  __is_entity_id: never;
  __name: Name;
};
export enum DisputeState {
  NONE = 'none',
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
}

export interface DisputeRow {
  id: number;
  reference_id: string;
  order_id: number;
  order_reference_id: string;
  provider_id: number;
  state: DisputeState;
  amount: number;
  currency: string;
  user_refund_ids: number[];
}
export interface GetDisputesPaginatedResult extends DbPaginatedQueryResponse<DisputeRow> {}

export interface GetRefundDisputesPaginatedRequest extends GetDisputesPaginatedData {
  language: string;
}

export interface GetRefundDisputesPaginatedResponse extends GetDisputesPaginatedResult {}

export interface CreateDisputeData {
  order_id: number;
  provider_id: number;
  user_refund_ids: number[];
  hash: string;
  additional_info?: string;
}

export interface CreateDisputeResult {
  id: number;
  does_dispute_already_exist?: boolean;
}

export interface CreateRefundDisputeRequest extends CreateDisputeData {}
export interface CreateRefundDisputeResponse extends CreateDisputeResult {}

export interface ReportsFilter {
  provider_order_states?: ProviderOrderState[];
  only_refunded_orders?: boolean;
}
export interface OrderHistoryFilter extends ReportsFilter {}

export enum OrderHistoryPagesSortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export interface OrderHistoryPagesQuery {
  start_ts?: number;
  end_ts?: number;
  limit?: number;
  page_size?: number;
  filter?: OrderHistoryFilter;
  sort_order?: OrderHistoryPagesSortOrder;
}

export interface GetOrderHistoryPagesResponse extends OrderHistoryPagesQueryResponse {}
export interface OrderHistoryPagesQueryResponse {
  total_order_count: number;
  totals_by_currency: Price[];
  pages: OrderHistoryPage[];
}
export interface OrderHistoryPage {
  start: number;
  end: number;
}
export interface GetOrderHistoryDetailsRequest extends GetOrderHistoryDetails {
  provider_id: number;
}
export interface GetOrderHistoryDetails {
  start: number;
  end: number;
  filter?: OrderHistoryFilter;
  include_refunds?: boolean;
}
export interface GetOrderHistoryDetailsResponse {
  orders: OrderHistoryProviderEntry[];
}
export interface OrderHistoryProviderEntry {
  order_id: number;
  order_reference_id: string;
  order_state: ProviderOrderState;
  order_fail_reason: ProviderOrderFailReason | null;
  order_final_state_transition_timestamp: number;
  order_waiting_confirmation_transition_timestamp: number;
  total_item_price: Price;
  total_price_breakdown: PriceBreakdown;
  eater: EaterInfoForPastOrder;
  courier: CourierInfoForPastOrder;
  baskets: ExternalBaseUserBasket[];
  payment_info_list: PaymentInfo[];
  order_type: DeliveryOrderType;
  delivery_type: DeliveryType;
  rating_value: number | null;
  rating_comment: string | null;
  rating_highlights: ProviderOrderRatingRawHighlight[];
  refund_info?: RefundInfo;
  waiter_tip?: Price;
}

export enum CreateDisputeUnavailableReason {
  ORDER_IS_TOO_OLD = 'ORDER_IS_TOO_OLD',
  DISPUTE_LIMIT_HAS_BEEN_REACHED = 'DISPUTE_LIMIT_HAS_BEEN_REACHED',
}
export interface CreateDisputeUnavailableStatus {
  reason: CreateDisputeUnavailableReason;
  parameter: number | string;
}

export interface OrderHistoryOrderDetails extends OrderHistoryProviderEntry {
  create_dispute_hash: string | null;
  create_dispute_unavailable_status: CreateDisputeUnavailableStatus | null;
}

export enum ProviderOrderState {
  DISPATCH_FIRST_INIT = 'dispatch_first_init',
  WAITING_CONFIRMATION = 'waiting_confirmation',
  ACCEPTED = 'accepted',
  WAITING_PREPARING_CONFIRMATION = 'waiting_preparing_confirmation',
  PREPARING = 'preparing',
  DID_NOT_RESPOND = 'did_not_respond',
  REJECTED = 'rejected',
  FAILED = 'failed',
  WAITING_PICKUP = 'waiting_pickup',
  PICKED_UP = 'picked_up',
}

export enum OrderEntity {
  ORDER = 'order',
  REFUND = 'refund',
  DISPUTE = 'dispute',
}

export interface GetOrderHistoryExtendedDetailsRequest {
  provider_id: number;
  order_id: number;
  entities?: OrderEntity[];
}
export interface OrderHistoryRefundsItem {
  item_id: number;
  quantity: number;
  name: LocalisedString;
}

export interface OrderHistoryRefundsInfo {
  refund_id: number;
  created: number;
  amount?: Price;
  total_amount_refunded?: Price;
  reason_list?: string[];
  fee_list: UserRefundFeeData[];
  type: UserRefundType;
  items: OrderHistoryRefundsItem[];
}

export interface OrderHistoryDisputeEntry {
  dispute_id: number;
  last_update: number;
  reference_id: string;
  amount: number;
  currency: string;
  order_id: number;
  order_reference_id: string;
  provider_id: number;
  user_refund_ids: number[];
  state: DisputeState;
}

export interface GetOrderHistoryExtendedDetailsResponse
  extends OrderHistoryProviderExtendedDetails {}

export interface OrderHistoryProviderExtendedDetails {
  order_id: number;
  order?: OrderHistoryOrderDetails;
  refunds?: OrderHistoryRefundsInfo[];
  disputes?: OrderHistoryDisputeEntry[];
}

export enum ProviderOrderFailReason {
  OUT_OF_STOCK = 'out_of_stock',
  CLIENT_CANCELLED = 'client_cancelled',
  CANNOT_FIND_COURIER = 'cannot_find_courier',
  REJECTED_DISPATCH_FIRST_ORDER = 'rejected_dispatch_first_order',
  DID_NOT_RESPOND_TO_DISPATCH_FIRST_ORDER = 'did_not_respond_to_dispatch_first_order',
  OTHER = 'other',
  MANUALLY_FAILED = 'manually_failed_by_cs',
}
export interface EaterInfoForPastOrder {
  user_id: number;
  is_happy?: boolean;
  partial_name: string;
  dine_in_info?: DineInEaterInfoForPastOrder;
}
export interface DineInEaterInfoForPastOrder {
  dining_table_id: number;
  is_paid: boolean;
  table_number?: number;
  table_name?: string;
}
export interface CourierInfoForPastOrder {
  courier_id: number | null;
  partial_name: string | null;
  picture_url: string | null;
}
export interface ExternalBaseUserBasket {
  user_basket_id: number;
  partial_user_name: string | null;
  partial_user_phone_number: string | null;
  user_note: string | null;
  items: ExternalBaseMenuItem[];
  total_item_price: PriceWithModifiers;
}
export interface ExternalBaseMenuItem {
  name: LocalisedString;
  menu_item_id: number;
  /**
   * Price for a single unit. This does not include the price of the options.
   */
  unit_item_price: PriceWithModifiers;
  /**
   * Total price for displaying in the app. This price includes everything for this entry (amount and options).
   * total_price = amount * (unit_price + total_price_of_item_options)
   *               = amount * (unit_price + amount_option_1 * unit_price_option_1 +
   *                                        amount_option_2 * unit_price_option_2 +
   *                                        ... )
   */
  total_item_price: PriceWithModifiers;
  amount: number;
  user_note: string | null;
  options: ExternalBaseMenuItemOption[] | null;
}
export interface PriceWithModifiers extends Price {
  original_price?: Price;
  modifiers?: PriceModifiers[];
}
export interface PriceModifiers {
  index: number;
  type: PriceModifierType;
}
export interface Campaign extends PriceModifiers {
  type: PriceModifierType.CAMPAIGN;
  campaign_token: string;
}
export enum PriceModifierType {
  CAMPAIGN = 'campaign',
  PROVIDER = 'provider',
}
export interface ProviderModifier extends PriceModifiers {
  type: PriceModifierType.PROVIDER;
}
export interface ExternalBaseMenuItemOption {
  menu_item_id: number;
  name: LocalisedString;
  /**
   * Total price for displaying in the app for this option.
   * total_price = amount * unit_price
   */
  total_item_price: PriceWithModifiers;
  /**
   * Price for a single unit.
   */
  unit_item_price: PriceWithModifiers;
  amount: number;
}

/**
 * Public interface, used in App endpoints.src/delivery/order/delivery_order/test/comp/admin/order-admin-comp
 */
export interface PaymentInfo {
  action: OrderPaymentAction;
  payment_method: OrderPaymentMethod;
  state?: OrderPaymentState;
}
export const OrderPaymentActionValues = {
  PICKUP: 'pickup',
  DROP_OFF: 'drop_off',
} as const;
export type OrderPaymentAction =
  typeof OrderPaymentActionValues[keyof typeof OrderPaymentActionValues];
export const OrderPaymentMethodValues = {
  CASH: 'cash',
  CASHLESS: 'cashless',
} as const;
export type OrderPaymentMethod =
  typeof OrderPaymentMethodValues[keyof typeof OrderPaymentMethodValues];
export const OrderPaymentStateValues = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  FINISHED: 'finished',
  FAILED: 'failed',
} as const;
export type OrderPaymentState =
  typeof OrderPaymentStateValues[keyof typeof OrderPaymentStateValues];

export enum DeliveryOrderType {
  REGULAR = 'regular',
  TAKEAWAY = 'takeaway',
  DISPATCH_FIRST = 'dispatch_first',
  PARALLEL = 'parallel',
}
export enum DeliveryType {
  PLATFORM = 'platform',
  OWN_DELIVERY = 'own_delivery',
  TAKEAWAY = 'takeaway',
  DINE_IN = 'dine_in',
}
export enum UserRefundFee {
  DELIVERY = 'delivery',
  SMALL_ORDER = 'small_order',
  COURIER_TIP = 'courier_tip',
  SERVICE = 'service',
}
export enum UserRefundType {
  FULL = 'full',
  PARTIAL = 'partial',
}
export interface UserRefundFeeData {
  type: UserRefundFee;
  amount: Price;
}
export interface UserRefund {
  user_refund_id: number;
  created: number;
  amount?: Price;
  total_amount_refunded: Price;
  reason_list?: string[];
  fee_list: UserRefundFeeData[];
  type: UserRefundType;
}
export interface RefundInfo {
  refunds: UserRefund[];
}
export interface EnsureDraftMenuRequest {
  provider_id: number;
}

export interface GetProviderDetailsRequest {
  provider_id: number;
}
export interface GetProviderDetailsResponse {
  details: ProviderPublicDetails;
}
export interface UpdateProviderDetailsRequest {
  provider_id: number;
  details: ProviderPublicDetails;
}
export interface ProviderPublicDetails {
  website: string | null;
}

export interface PublishDraftMenuRequest {
  provider_id: number;
  menu_id: string;
  seconds_until_linking: number;
  selected_provider_id?: number;
  target_provider_ids?: number[];
}

export interface MenuModifiedRequest {
  provider_id: number;
  menu_id: string;
}

export interface MenuModifiedResponse {
  is_modified: boolean;
}

export interface CreateProviderMenuTimetableRequest {
  provider_id: number;
  timetable: ProviderMenuTimetable;
}

export interface ProviderMenuTimetable {
  name: string;
  description: string | null;
  availability: ProviderMenuTimetableAvailability;
}

export interface ProviderMenuTimetableAvailability {
  [day: number]: ProviderMenuTimetableDayAvailability[];
}

export interface ProviderMenuTimetableDayAvailability {
  seconds_from_midnight_from: number;
  seconds_from_midnight_to: number;
}

export interface CreateProviderMenuTimetableResponse {
  timetable_id: string;
}

export interface UpdateProviderMenuTimetableRequest extends CreateProviderMenuTimetableRequest {
  timetable_id: string;
}

export interface DeleteProviderMenuTimetableRequest {
  provider_id: number;
  timetable_id: string;
}

export interface GetProviderMenuTimetableRequest {
  provider_id: number;
  timetable_id: string;
}

export interface GetProviderMenuTimetableResponse
  extends ProviderMenuTimetablesWithMenuItemsAssignments {}

export interface GetProviderMenuTimetablesRequest {
  provider_id: number;
}

export interface GetProviderMenuTimetablesResponse {
  timetables: ProviderMenuTimetablesWithMenuItemsAssignments[];
}
export interface ProviderMenuTimetablesWithMenuItemsAssignments
  extends ProviderMenuTimetableWithId {
  menu_items: TimetableMenuItem[];
}
export interface ProviderMenuTimetableWithId extends ProviderMenuTimetable {
  id: string;
}
export interface TimetableMenuItem {
  menu_item_trace_id: string;
  menu_item_name: TranslatableField;
  menu_item_images: MenuItemImagesResponse | null;
}

export interface CancelFutureMenuLinkRequest {
  provider_id: number;
  link_id: number;
}
export interface GetVendorProviderAndMenuLinksRequest {
  vendor_id: number;
  provider_ids: number[];
}

export interface GetVendorProviderAndMenuLinksResponse {
  provider_links: ProviderAndMenuLinksResponse[];
}

export interface ProviderAndMenuLinksResponse {
  provider_id: number;
  name: TranslatableField;
  upcoming_link?: MenuLinkResponse;
  previous_link?: MenuLinkResponse;
}

export interface MenuLinkResponse {
  link_id: number;
  menu_id: number;
  menu_name: TranslatableField;
  link_state: MenuItemScheduledEventState.PENDING | MenuItemScheduledEventState.COMPLETED;
  when: number;
}

export enum MenuItemScheduledEventState {
  PENDING = 'pending',
  CANCELED = 'canceled',
  COMPLETED = 'completed',
  DELETED = 'deleted',
}

export interface ProviderMenuLinksRequest {
  provider_id: number;
}

export interface ProviderMenuLinksResponse {
  current_link?: MenuLink;
  upcoming_links: MenuLink[];
  previous_links: MenuLink[];
}

export interface MenuLink {
  link_id: number;
  menu_id: number;
  link_timestamp: number;
  menu_name: TranslatableField;
}

export interface OverwriteDraftMenuRequest {
  provider_id: number;
  base_menu_id: string;
  new_menu_name: TranslatableField;
}

export interface OverwriteDraftMenuResponse {
  menu_id: string;
}

export interface Locale {
  language: string;
  country: string;
}
export interface WeeklyPeriod {
  week_nr: number;
  year: number;
  type: 'weekly';
}

export interface GetReportFileInput {
  provider_id: number;
  locale: Locale;
  period: WeeklyPeriod;
  format: PartnerReportFormat;
}

export enum PartnerReportFormat {
  PDF = 'pdf',
  CSV = 'csv',
  XLSX = 'xlsx',
}

export interface DownloadProviderSalesReportRequest {
  provider_id: number;
  locale: Locale;
  period: WeeklyPeriod;
  format: PartnerReportFormat;
}

export interface DownloadProviderSalesReportResponse {
  file_name: string;
  file_content: string;
  file_content_encoding: string;
  file_type: string;
}

export interface GetProviderInvoicesRequest {
  provider_id: number;
}

export interface GetProviderInvoicesResponse {
  invoices: ProviderInvoiceRow[];
}

export interface ProviderInvoiceRow {
  invoice_id: string;
  period_start: number | null;
  period_end: number | null;
  currency: string;
  currency_symbol: string;
  total_sum: number;
  total_vat_sum: number;
  total_sum_with_vat: number;
  city_id: number;
  city_local_name: string | null;
  city_name: string | null;
}

export interface DownloadProviderInvoiceRequest {
  provider_id: number;
  invoice_id: string;
  locale: Locale;
}

export interface DownloadProviderInvoiceResponse {
  url: string;
}

export enum FileExportType {
  CSV = 'csv',
  XLSX = 'xlsx',
}
export enum ReportType {
  ORDERS = 'orders',
  ORDER_ITEMS = 'order_items',
  MARKETING_CONSENTS = 'marketing_consents',
  GENERAL_RESTAURANT = 'general_restaurant',
  AMREST_TRANSACTION = 'amrest_transaction',
}

export interface GenerateProviderDetailedReportRequest {
  provider_ids: number[];
  start_interval_timestamp_seconds: number;
  end_interval_timestamp_seconds: number;
  export_type: FileExportType;
  report_type: ReportType.ORDERS | ReportType.ORDER_ITEMS;
}

export interface GenerateProviderDetailedReportResponse {
  report_id: number;
}

export interface GetDetailedReportsStatusesRequest {
  report_ids: number[];
}
export interface GetDetailedReportsStatusesResponse {
  report_statuses: DetailedReportStatus[];
}
export enum ProviderPortalReportStatus {
  IN_PROGRESS = 'in_progress',
  FAILED = 'failed',
  GENERATED = 'generated',
}
export interface DetailedReportStatus {
  report_id: number;
  status: ProviderPortalReportStatus;
}

export interface GetDownloadLinkForDetailedReportRequest {
  report_id: number;
}

export interface GetDownloadLinkForDetailedReportResponse {
  report_link: string;
  report_filename: string;
}

export interface GetDetailedReportsListForAccountRequest {
  report_type?: ReportType[];
  limit?: number;
}
export interface GetDetailedReportsListForAccountResponse {
  reports: DetailedReportListEntry[];
}
export interface DetailedReportListEntry {
  report_id: number;
  report_type: ReportType;
  start_interval_timestamp_seconds: number;
  end_interval_timestamp_seconds: number;
  export_type: FileExportType;
  status: ProviderPortalReportStatus;
}
export interface GetSupportWidgetUrlRequest {
  provider_id: number;
  city_id: number;
}
export interface GetSupportWidgetUrlResponse {
  url: string;
}

export interface GetSupportCaseWidgetUrlRequest {
  provider_id: number;
  city_id: number;
}
export interface GetSupportCaseWidgetUrlResponse {
  url: string;
}

export interface GetSupportUnreadCasesWithLastMessageRequest {
  provider_id: number;
}

export interface UnreadCaseWithLastMessage {
  case_id: number;
  last_message: string;
}
export interface GetSupportUnreadCasesWithLastMessageResponse {
  cases: UnreadCaseWithLastMessage[];
}

export interface GetSupportWidgetMqttConnectionInfoRequest {
  provider_id: number;
}

export interface GetSupportWidgetMqttConnectionInfoResponse {
  user_id: number;
  broker_url: string;
}

export type SupportMqttConnInfo = GetSupportWidgetMqttConnectionInfoResponse;

export interface SelectedSupportCase {
  case_id: number;
  provider_id: number;
}

export interface GetTargetingParametersForProviderRequest {
  provider_id: number;
  city_id: number;
  country_code?: string;
}
export interface GetTargetingParametersForProviderResponse {
  parameters: { [targetingExperimentName: string]: any };
}
export interface Parameters {
  [eventName: string]: {
    [featureOrFlagName: string]: any;
  };
}
export interface GetProviderAvailabilityModeListRequest
  extends ProviderAvailabilityModeFilters,
    ProviderAvailabilityModeSorting,
    ListRequestBase {
  language: string;
}

export interface ListRequestBase {
  offset?: number;
  limit?: number;
}

export interface ProviderAvailabilityModeFilters {
  provider_id: number;
  from_timestamp?: number;
  to_timestamp?: number;
  availability_mode?: ProviderAvailabilityMode[];
  availability_change_reason?: AvailabilityModeChangeReason[];
  offline_availability_reason?: string[];
}

export interface ProviderAvailabilityModeSorting {
  order_by_field?: ProviderAvailabilityModeOrderByField;
  order_by_direction?: OrderByDirection;
}

export enum OrderByDirection {
  DESC = 'DESC',
}

export enum ProviderAvailabilityModeOrderByField {
  CREATED = 'created',
}

export interface GetProviderAvailabilityModeListResponse extends ListResponseBase {
  provider_availability_mode_rows: ProviderAvailabilityModeRow[];
}

export enum ProviderAvailabilityMode {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  BUSY = 'busy',
}

export interface ListResponseBase {
  result_count: number;
}

export interface ProviderAvailabilityModeRow {
  id: number;
  created_timestamp: number;
  availability_mode: ProviderAvailabilityMode;
  availability_change_reason: AvailabilityModeChangeReason;
  offline_availability_reason: string | null;
}

export interface GetMenuItemsForProviderActiveMenuRequest {
  provider_id: number;
}

export interface GetMenuItemsForProviderActiveMenuResponse {
  items_data: MenuItemData[];
}

export interface MenuItemData {
  name: TranslatableField;
  icon: string | null;
  tags: string[];
  price: number;
  currency: string;
  price_str: string;
  global_stable_context_id: string | null;
  product_id: string | null;
}

export interface GetProvidersAIMenuTranslationsAccessRequest {
  provider_ids: number[];
}

export interface GetProvidersAIMenuTranslationsAccessResponse {
  provider_ai_menu_translations_map: {
    [providerId: number]: boolean;
  };
}

/**
 * The same as OperationalStatusChangeReason
 */
export enum AvailabilityModeChangeReason {
  ADMIN_PANEL_CHANGE = 'admin_panel_change',
  PROVIDER_APP_CHANGE = 'provider_app_change',
  DROPPED_POLLING = 'dropped_polling',
  RESUMED_POLLING = 'resumed_polling',
  DID_NOT_RESPOND_TO_ORDER = 'did_not_respond_to_order',
  INTEGRATOR_APP_CHANGE = 'integrator_app_change',
  SF_APP_CHANGE = 'sf_app_change',
  PICKER_APP_CHANGE = 'picker_app_change',
}

export interface GetProviderOfflinePeriodsRequest {
  provider_id: number;
  statuses: ProviderOfflinePeriodStatus[];
}

export enum ProviderOfflinePeriodStatus {
  PENDING = 'pending',
  STARTED = 'started',
  ENDED = 'ended',
  CANCELLED = 'cancelled',
}

export interface GetProviderOfflinePeriodsResponse {
  offline_periods: ProviderOfflinePeriod[];
}

export interface ProviderOfflinePeriod {
  id: number;
  status: ProviderOfflinePeriodStatus;
  start_date: string;
  end_date: string;
}

export interface CreateProviderOfflinePeriodRequest {
  provider_id: number;
  start_date: string;
  end_date: string;
}

export interface CreateProviderOfflinePeriodResponse {
  offline_period_id: number;
}

export interface CancelProviderOfflinePeriodRequest {
  provider_id: number;
  offline_period_id: number;
}

export interface FeedbackConfigBase {
  type: FeedbackType;
  translated_name: string;
  fields: Field[];
  delay_timeout_ms: number;
  on_submit_text: string;
  on_delay_text: string;
  on_skip_text: string;
  skip_action_text: string;
  answer_later_action_text: string;
}
export interface FeedbackConfigOrderVolumeExpectations extends FeedbackConfigBase {
  type: FeedbackType.ORDER_VOLUME_EXPECTATIONS;
}
export interface FeedbackResult {
  fields: FieldValue[];
}
export enum FeedbackType {
  ORDER_VOLUME_EXPECTATIONS = 'order_volume_expectations',
}
export type Field = PositiveIntegerField | StringField;
export interface FieldBase {
  type: FieldType;
  id: FieldId;
  translated_name: string;
  translated_question: string;
  value?: number | string;
  is_optional?: boolean;
}
export enum FieldId {
  ORDER_EXPECTED_VOLUME_PER_WEEK = 'order_expected_volume_per_week',
  ORDER_MIN_VOLUME_PER_WEEK = 'order_min_volume_per_week',
  ORDER_COMPETITOR_VOLUME_PER_WEEK = 'order_competitor_volume_per_week',
}
export enum FieldType {
  PositiveIntegerType = 'positive_integer',
  StringType = 'string',
}
export interface FieldValue {
  id: FieldId;
  value: number | string;
}
export interface PositiveIntegerField extends FieldBase {
  type: FieldType.PositiveIntegerType;
  value?: number;
}
export interface StringField extends FieldBase {
  type: FieldType.StringType;
  value?: string;
}

export interface ShouldGetFeedbackPortalRequest {
  provider_id: number;
}
export interface ShouldGetFeedbackPortalResponse {
  should_get_feedback: boolean;
  feedback_config?: FeedbackConfigOrderVolumeExpectations;
}

export interface SetFeedbackPortalRequest {
  provider_id: number;
  feedback_type: FeedbackType;
  feedback_result: FeedbackResult;
}

export interface SkipFeedbackPortalRequest {
  provider_id: number;
  feedback_type: FeedbackType;
}

export class DeliveryProviderPortalAuthApi extends Gateway {
  public uploadMenuCSV = async (
    request: MenuManagementCsvUpdateRequest,
  ): Promise<CSVImportResponse> => {
    return this.postJSON<MenuManagementCsvUpdateRequest, CSVImportResponse>(
      `/deliveryProviderPortal/menuItemAvailabilityUpdateCsvUpload`,
      request,
    );
  };

  public getCampaignTagsConfig = async (
    request: Record<never, never>,
  ): Promise<CampaignTagsConfigResponse> => {
    return this.get(`/deliveryProviderPortal/getCampaignTagsConfig`, request);
  };

  public getPreselectedOptionsEnablementConfig = async (
    request: Record<never, never>,
  ): Promise<PreselectedOptionsEnablementConfigResponse> => {
    return this.get(`/deliveryProviderPortal/getPreselectedOptionsEnablementConfig`, request);
  };

  public getMenu = async (request: ProviderMenuRequest): Promise<ProviderMenuResponse> => {
    return this.get<ProviderMenuRequest, ProviderMenuResponse>(
      `/deliveryProviderPortal/getMenu`,
      request,
    );
  };

  public updateMenu = async (
    request: MenuItemsBulkUpdateRequest,
  ): Promise<ProviderUpdateMenuItemAvailabilityResponse> => {
    return this.postJSON<MenuItemsBulkUpdateRequest, ProviderUpdateMenuItemAvailabilityResponse>(
      `/deliveryProviderPortal/menuItemsBulkUpdate`,
      request,
    );
  };

  public updateMenuItemAvailability = async (
    request: ProviderUpdateMenuItemAvailabilityRequest,
  ): Promise<ProviderUpdateMenuItemAvailabilityResponse> => {
    return this.postJSON<
      ProviderUpdateMenuItemAvailabilityRequest,
      ProviderUpdateMenuItemAvailabilityResponse
    >('/deliveryProviderPortal/updateMenuItemAvailability', request);
  };

  public getProviderSchedule = async (
    request: GetProviderScheduleRequest,
  ): Promise<GetProviderScheduleResponse> => {
    return this.get<GetProviderScheduleRequest, GetProviderScheduleResponse>(
      '/deliveryProviderPortal/getProviderSchedule',
      request,
    );
  };

  public updateProviderSchedule = async (request: UpdateProviderScheduleRequest): Promise<void> => {
    return this.postJSON<UpdateProviderScheduleRequest, Promise<void>>(
      '/deliveryProviderPortal/updateProviderSchedule',
      request,
    );
  };

  public downloadProvidersReport = async (
    request: DownloadProvidersReportRequest,
  ): Promise<FileResponse> => {
    return this.postJSON<DownloadProvidersReportRequest, FileResponse>(
      `/deliveryProviderPortal/downloadProvidersReport`,
      request,
      {
        timeout: TIMEOUT_60000_MS,
      },
    );
  };

  public generateDetailedReport = async (
    request: GenerateProviderDetailedReportRequest,
  ): Promise<GenerateProviderDetailedReportResponse> => {
    return this.postJSON<
      GenerateProviderDetailedReportRequest,
      GenerateProviderDetailedReportResponse
    >(`/deliveryProviderPortal/generateDetailedReport`, request);
  };

  public getDetailedReportStatuses = async (
    request: GetDetailedReportsStatusesRequest,
  ): Promise<GetDetailedReportsStatusesResponse> => {
    return this.postJSON<GetDetailedReportsStatusesRequest, GetDetailedReportsStatusesResponse>(
      `/deliveryProviderPortal/getDetailedReportsStatuses`,
      request,
    );
  };

  public getDownloadLinkForDetailedReport = async (
    request: GetDownloadLinkForDetailedReportRequest,
  ): Promise<GetDownloadLinkForDetailedReportResponse> => {
    return this.postJSON<
      GetDownloadLinkForDetailedReportRequest,
      GetDownloadLinkForDetailedReportResponse
    >(`/deliveryProviderPortal/getDownloadLinkForDetailedReport`, request);
  };

  public getDetailedReportsListForAccount = async (
    request: GetDetailedReportsListForAccountRequest,
  ): Promise<GetDetailedReportsListForAccountResponse> => {
    return this.postJSON<
      GetDetailedReportsListForAccountRequest,
      GetDetailedReportsListForAccountResponse
    >(`/deliveryProviderPortal/getDetailedReportsListForAccount`, request);
  };

  public downloadProvidersItemsReport = async (
    request: DownloadProvidersReportRequest,
  ): Promise<FileResponse> => {
    return this.postJSON<DownloadProvidersReportRequest, FileResponse>(
      `/deliveryProviderPortal/downloadProvidersItemsReport`,
      request,
      {
        timeout: TIMEOUT_60000_MS,
      },
    );
  };

  public downloadProviderWeeklyReportCsv = async (
    request: GetProviderWeeklyReportCsvRequest,
  ): Promise<FileResponse> => {
    return this.postJSON<GetProviderWeeklyReportCsvRequest, FileResponse>(
      `/deliveryProviderPortal/downloadProviderWeeklyReportCsv`,
      request,
    );
  };

  public getProviderWeeklyReport = async (
    request: GetProviderWeeklyReportRequest,
  ): Promise<GetProviderWeeklyReportResponse> => {
    return this.postJSON<GetProviderWeeklyReportRequest, GetProviderWeeklyReportResponse>(
      `/deliveryProviderPortal/getProviderWeeklyReport`,
      request,
    );
  };

  public getProviderWeeklyReportList = async (
    request: GetProviderWeeklyReportListRequest,
  ): Promise<GetProviderWeeklyReportListResponse> => {
    return this.postJSON<GetProviderWeeklyReportListRequest, GetProviderWeeklyReportListResponse>(
      `/deliveryProviderPortal/getProviderWeeklyReportList`,
      request,
    );
  };

  public getProviderStatistics = async (
    request: GetProviderStatisticsRequest,
  ): Promise<GetProviderStatisticsResponse> => {
    return this.postJSON<GetProviderStatisticsRequest, Promise<GetProviderStatisticsResponse>>(
      '/deliveryProviderPortal/getProviderStatistics',
      request,
    );
  };

  public getOrderHistoryPages = async (
    request: GetOrderHistoryPagesRequest,
  ): Promise<GetOrderHistoryPagesResponse> => {
    return this.postJSON<GetOrderHistoryPagesRequest, GetOrderHistoryPagesResponse>(
      '/deliveryProviderPortal/getOrderHistoryPages',
      request,
    );
  };

  public getLatestOnboardingProviderForAccount = async (
    request: GetLatestOnboardingProviderForAccountRequest,
  ): Promise<GetLatestOnboardingProviderForAccountResponse> => {
    return this.postJSON('/deliveryProviderPortal/getLatestOnboardingProviderForAccount', request);
  };

  public getOrderHistoryDetails = async (
    request: GetOrderHistoryDetailsRequest,
  ): Promise<GetOrderHistoryDetailsResponse> => {
    return this.postJSON<GetOrderHistoryDetailsRequest, GetOrderHistoryDetailsResponse>(
      '/deliveryProvider/getOrderHistoryDetails',
      request,
    );
  };

  public getOrderHistoryExtendedDetails = async (
    request: GetOrderHistoryExtendedDetailsRequest,
  ): Promise<GetOrderHistoryExtendedDetailsResponse> => {
    return this.postJSON<
      GetOrderHistoryExtendedDetailsRequest,
      GetOrderHistoryExtendedDetailsResponse
    >('/deliveryProviderPortal/getOrderHistoryExtendedDetails', request);
  };

  public getDisputesPaginated = async (
    request: GetRefundDisputesPaginatedRequest,
  ): Promise<GetRefundDisputesPaginatedResponse> => {
    return this.get<GetRefundDisputesPaginatedRequest, GetRefundDisputesPaginatedResponse>(
      '/deliveryProviderPortal/getRefundDisputesPaginated',
      request,
    );
  };

  public createRefundDispute = async (
    request: CreateRefundDisputeRequest,
  ): Promise<CreateRefundDisputeResponse> => {
    return this.postJSON<CreateRefundDisputeRequest, CreateRefundDisputeResponse>(
      '/deliveryProviderPortal/createRefundDispute',
      request,
    );
  };

  public getProviderMenus = async (
    request: GetProviderMenusRequest,
  ): Promise<HasAccessToMenuEditorResponse> => {
    return this.postJSON('/deliveryProviderPortal/getProviderMenus', request);
  };

  public getMenuInfo = async (request: GetMenuInfoRequest): Promise<GetMenuInfoResponse> => {
    return this.postJSON('/deliveryProviderPortal/getMenuInfo', request);
  };

  public getMenuChildEntities = async (
    request: GetChildEntitiesRequest,
  ): Promise<GetChildEntitiesResponse> => {
    return this.postJSON('/deliveryProviderPortal/getMenuChildEntities', request);
  };

  public getMenuItemsChildrenNumber = async (
    request: MenuItemsChildrenCountRequest,
  ): Promise<MenuItemsChildrenCountResponse> => {
    return this.postJSON('/deliveryProviderPortal/getMenuItemsChildrenNumber', request);
  };

  public getMenuEntities = async (
    request: GetMenuEntitiesRequest,
  ): Promise<GetMenuEntitiesResponse> => {
    return this.postJSON('/deliveryProviderPortal/getMenuEntities', request);
  };

  public getMenuEditorFeatureFlags = async (
    request: Record<never, never>,
  ): Promise<GetMenuEditorFeatureFlagsResponse> => {
    return this.postJSON('/deliveryProviderPortal/getMenuEditorFeatureFlags', request);
  };

  public getMenuEntitiesWithAgeRestrictions = async (
    request: GetMenuEntitiesRequest,
  ): Promise<GetMenuEntitiesWithAgeRestrictionsResponse> => {
    return this.postJSON('/deliveryProviderPortal/getMenuEntitiesWithAgeRestrictions', request);
  };

  public getMenuEntity = async (request: GetMenuEntityRequest): Promise<GetMenuEntityResponse> => {
    return this.postJSON('/deliveryProviderPortal/getMenuEntity', request);
  };

  public createMenuEntity = async (
    request: CreateMenuEntityRequest,
  ): Promise<CreateMenuEntityResponse> => {
    return this.postJSON('/deliveryProviderPortal/createMenuEntity', request);
  };

  public updateMenuEntity = async (
    request: UpdateMenuEntityRequest,
  ): Promise<UpdateMenuEntityResponse> => {
    return this.postJSON('/deliveryProviderPortal/updateMenuEntity', request);
  };

  public bulkUpdateMenuEntity = async (request: BulkUpdateMenuEntityRequest): Promise<void> => {
    return this.postJSON('/deliveryProviderPortal/bulkUpdateMenuEntity', request);
  };

  public deleteMenuItem = async (request: DeleteMenuItemRequest): Promise<void> => {
    return this.postJSON('/deliveryProviderPortal/deleteMenuItem', request);
  };

  public bulkDeleteMenuItems = async (request: BulkDeleteMenuItemsRequest): Promise<void> => {
    return this.postJSON('/deliveryProviderPortal/bulkDeleteMenuItems', request);
  };

  public deleteChildEntity = async (request: DeleteChildEntityRequest): Promise<void> => {
    return this.postJSON('/deliveryProviderPortal/deleteChildEntity', request);
  };

  public ensureDraftMenu = async (request: EnsureDraftMenuRequest): Promise<void> => {
    return this.postJSON('/deliveryProviderPortal/ensureDraftMenu', request);
  };

  public createEmptyDraftMenu = async (request: EmptyMenuRequest): Promise<EmptyMenuResponse> => {
    return this.postJSON('/deliveryProviderPortal/createEmptyMenu', request);
  };

  public isBulkUploadEnabledForProviderPortalAccount = async (
    request: Record<never, never>,
  ): Promise<IsMenuBulkUploadEnabledForProviderPortalAccountResponse> => {
    return this.get(
      '/deliveryProviderPortal/isMenuBulkUploadEnabledForProviderPortalAccount',
      request,
    );
  };

  public getProvidersAIMenuTranslationsAccess = async (
    request: GetProvidersAIMenuTranslationsAccessRequest,
  ): Promise<GetProvidersAIMenuTranslationsAccessResponse> => {
    return this.postJSON('/deliveryProviderPortal/getProvidersAIMenuTranslationsAccess', request);
  };

  public bulkUploadMenuItemImagesFromCSV = async (
    request: BulkUploadMenuItemImagesFromCSVRequest,
  ): Promise<BulkUploadMenuItemImagesFromCSVResponse> => {
    return this.postJSON('/deliveryProviderPortal/bulkUploadMenuItemImagesFromCSV', request);
  };

  public downloadProviderDraftMenusCSV = async (
    request: DownloadProviderDraftMenusCSVRequest,
  ): Promise<DownloadProviderDraftMenusCSVResponse> => {
    return this.postJSON('/deliveryProviderPortal/downloadProviderDraftMenusCSV', request);
  };

  public getAvailableTagsForClient = async (
    request: GetAvailableTagsForClientRequest,
  ): Promise<GetAvailableTagsForClientResponse> => {
    return this.postJSON('/deliveryProviderPortal/getAvailableTagsForClient', request);
  };

  public updateMenuItemsDisplayOrder = async (
    request: UpdateMenuItemsDisplayOrderRequest,
  ): Promise<void> => {
    return this.postJSON('/deliveryProviderPortal/updateMenuItemsDisplayOrder', request);
  };

  public deleteMenuEntityImage = async (request: MenuEntityImageRequest): Promise<void> => {
    return this.postJSON('/deliveryProviderPortal/deleteMenuEntityImages', request);
  };

  public updateMenuEntityImage = async (formData: FormData): Promise<MenuEntityImageResponse> => {
    const auth = new JwtAuth(this.config);
    const controller = new AbortController();
    const timeoutHandle = setTimeout(() => controller.abort(), this.config.defaultTimeout);

    const requestUrl = this.buildRequestUrl('/deliveryProviderPortal/updateMenuEntityImage');
    const queryString = objectToQueryString({ ...this.config.selectors?.getDefaultQueryParams() });
    const url = `${requestUrl}?${queryString}`;

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: { ...(await auth.getAuthHeader()) },
      signal: controller.signal,
    });

    clearTimeout(timeoutHandle);

    try {
      const responseText = await response.text();
      const json = JSON.parse(responseText) as MenuEntityImageResponse;
      return json;
    } catch (e: any) {
      throw new RequestError(e.message || 'JSON parsing failed', requestUrl);
    }
  };

  public updateProviderDetails = async (request: UpdateProviderDetailsRequest): Promise<void> => {
    return this.postJSON<UpdateProviderDetailsRequest, Promise<void>>(
      '/deliveryProviderPortal/updateProviderDetails',
      request,
    );
  };

  public getProviderDetails = async (
    request: GetProviderDetailsRequest,
  ): Promise<GetProviderDetailsResponse> => {
    return this.get<GetProviderDetailsRequest, GetProviderDetailsResponse>(
      '/deliveryProviderPortal/getProviderDetails',
      request,
    );
  };

  public getMenuPreview = async (
    request: GetMenuPreviewRequest,
  ): Promise<GetMenuPreviewResponse> => {
    return this.postJSON('/deliveryProviderPortal/getMenuPreview', request);
  };

  public publishMenu = async (request: PublishDraftMenuRequest): Promise<void> => {
    return this.postJSON<PublishDraftMenuRequest, Promise<void>>(
      '/deliveryProviderPortal/publishMenu',
      request,
    );
  };

  public isMenuModified = async (request: MenuModifiedRequest): Promise<MenuModifiedResponse> => {
    return this.postJSON<MenuModifiedRequest, Promise<MenuModifiedResponse>>(
      '/deliveryProviderPortal/isMenuModified',
      request,
    );
  };

  public createProviderMenuTimetable = async (
    request: CreateProviderMenuTimetableRequest,
  ): Promise<CreateProviderMenuTimetableResponse> => {
    return this.postJSON<
      CreateProviderMenuTimetableRequest,
      Promise<CreateProviderMenuTimetableResponse>
    >('/deliveryProviderPortal/createProviderMenuTimetable', request);
  };

  public updateProviderMenuTimetable = async (
    request: UpdateProviderMenuTimetableRequest,
  ): Promise<void> => {
    return this.postJSON<CreateProviderMenuTimetableRequest, Promise<void>>(
      '/deliveryProviderPortal/updateProviderMenuTimetable',
      request,
    );
  };

  public deleteProviderMenuTimetable = async (
    request: DeleteProviderMenuTimetableRequest,
  ): Promise<void> => {
    return this.postJSON<DeleteProviderMenuTimetableRequest, Promise<void>>(
      '/deliveryProviderPortal/deleteProviderMenuTimetable',
      request,
    );
  };

  public getProviderMenuTimetables = async (
    request: GetProviderMenuTimetablesRequest,
  ): Promise<GetProviderMenuTimetablesResponse> => {
    return this.get<GetProviderMenuTimetablesRequest, GetProviderMenuTimetablesResponse>(
      '/deliveryProviderPortal/getProviderMenuTimetables',
      request,
    );
  };

  public getProviderMenuTimetable = async (
    request: GetProviderMenuTimetableRequest,
  ): Promise<GetProviderMenuTimetableResponse> => {
    return this.get<GetProviderMenuTimetableRequest, GetProviderMenuTimetableResponse>(
      '/deliveryProviderPortal/getProviderMenuTimetable',
      request,
    );
  };

  public getVendorProviderAndMenuLinks = async (
    request: GetVendorProviderAndMenuLinksRequest,
  ): Promise<GetVendorProviderAndMenuLinksResponse> => {
    return this.postJSON<
      GetVendorProviderAndMenuLinksRequest,
      GetVendorProviderAndMenuLinksResponse
    >('/deliveryProviderPortal/getVendorProviderAndMenuLinks', request);
  };

  public getProviderMenuLinks = async (
    request: ProviderMenuLinksRequest,
  ): Promise<ProviderMenuLinksResponse> => {
    return this.postJSON<ProviderMenuLinksRequest, ProviderMenuLinksResponse>(
      '/deliveryProviderPortal/getProviderMenuLinks',
      request,
    );
  };

  public overwriteDraftMenu = async (
    request: OverwriteDraftMenuRequest,
  ): Promise<OverwriteDraftMenuResponse> => {
    return this.postJSON<OverwriteDraftMenuRequest, OverwriteDraftMenuResponse>(
      '/deliveryProviderPortal/overwriteDraftMenu',
      request,
    );
  };

  public cancelFutureMenuLinkById = async (request: CancelFutureMenuLinkRequest): Promise<void> => {
    return this.postJSON<CancelFutureMenuLinkRequest, void>(
      '/deliveryProviderPortal/cancelFutureMenuLinkById',
      request,
    );
  };

  public downloadProviderSalesReport = async (
    request: DownloadProviderSalesReportRequest,
  ): Promise<DownloadProviderSalesReportResponse> => {
    return this.postJSON('/deliveryProviderPortal/downloadProviderSalesReport', request);
  };

  public downloadProviderInvoice = async (
    request: DownloadProviderInvoiceRequest,
  ): Promise<DownloadProviderInvoiceResponse> => {
    return this.postJSON('/deliveryProviderPortal/downloadProviderInvoice', request);
  };

  public getProviderInvoices = async (
    request: GetProviderInvoicesRequest,
  ): Promise<GetProviderInvoicesResponse> => {
    return this.postJSON('/deliveryProviderPortal/getProviderInvoices', request);
  };

  public getMainEntryWidgetUrl = async (
    request: GetSupportWidgetUrlRequest,
  ): Promise<GetSupportWidgetUrlResponse> => {
    return this.postJSON('/deliveryProviderPortal/getMainEntryWidgetUrl', request);
  };

  public getSupportCaseWidgetUrl = async (
    request: GetSupportCaseWidgetUrlRequest,
  ): Promise<GetSupportCaseWidgetUrlResponse> => {
    return this.postJSON('/deliveryProviderPortal/getSupportCaseWidgetUrl', request);
  };

  public getUnreadCasesWithLastMessage = async (
    request: GetSupportUnreadCasesWithLastMessageRequest,
  ): Promise<GetSupportUnreadCasesWithLastMessageResponse> => {
    return this.postJSON('/deliveryProviderPortal/getUnreadCasesWithLastMessage', request);
  };

  public getSupportWidgetMqttConnectionInfo = async (
    request: GetSupportWidgetMqttConnectionInfoRequest,
  ): Promise<GetSupportWidgetMqttConnectionInfoResponse> => {
    return this.postJSON('/deliveryProviderPortal/getSupportWidgetMqttConnectionInfo', request);
  };

  public getTargetingValuesForProvider = async (
    request: DeliveryProviderPortalService.GetTargetingValuesForProviderRequest,
  ): Promise<DeliveryProviderPortalService.GetTargetingValuesForProviderResponse> => {
    return this.postJSON('/deliveryProviderPortal/getTargetingValuesForProvider', request);
  };

  public getProviderAvailabilityModeList = async (
    request: GetProviderAvailabilityModeListRequest,
  ): Promise<GetProviderAvailabilityModeListResponse> => {
    return this.postJSON<
      GetProviderAvailabilityModeListRequest,
      GetProviderAvailabilityModeListResponse
    >('/deliveryProviderPortal/getProviderAvailabilityModeList', request);
  };

  public getMenuItemsForProviderActiveMenu = async (
    request: GetMenuItemsForProviderActiveMenuRequest,
  ): Promise<GetMenuItemsForProviderActiveMenuResponse> => {
    return this.postJSON<
      GetMenuItemsForProviderActiveMenuRequest,
      GetMenuItemsForProviderActiveMenuResponse
    >('/deliveryProviderPortal/getMenuItemsForProviderActiveMenu', request);
  };

  public getOpenImprovementTrackEnrollments = async (
    request: DeliveryProviderPortalService.GetOpenImprovementTrackEnrollmentsRequest,
  ): Promise<DeliveryProviderPortalService.GetOpenImprovementTrackEnrollmentsResponse> => {
    return this.postJSON<
      DeliveryProviderPortalService.GetOpenImprovementTrackEnrollmentsRequest,
      DeliveryProviderPortalService.GetOpenImprovementTrackEnrollmentsResponse
    >('/deliveryProviderPortal/getOpenImprovementTrackEnrollments', request);
  };

  public hideImprovementTrackEnrollment = async (
    request: DeliveryProviderPortalService.HideImprovementTrackEnrollmentRequest,
  ): Promise<void> => {
    return this.postJSON<DeliveryProviderPortalService.HideImprovementTrackEnrollmentRequest, void>(
      '/deliveryProviderPortal/hideImprovementTrackEnrollment',
      request,
    );
  };

  public getProviderOfflinePeriods = async (
    request: GetProviderOfflinePeriodsRequest,
  ): Promise<GetProviderOfflinePeriodsResponse> => {
    return this.postJSON<GetProviderOfflinePeriodsRequest, GetProviderOfflinePeriodsResponse>(
      '/deliveryProviderPortal/getOfflinePeriods',
      request,
    );
  };

  public createProviderOfflinePeriod = async (
    request: CreateProviderOfflinePeriodRequest,
  ): Promise<CreateProviderOfflinePeriodResponse> => {
    return this.postJSON<CreateProviderOfflinePeriodRequest, CreateProviderOfflinePeriodResponse>(
      '/deliveryProviderPortal/createOfflinePeriod',
      request,
    );
  };

  public cancelProviderOfflinePeriod = async (
    request: CancelProviderOfflinePeriodRequest,
  ): Promise<void> => {
    return this.postJSON<CancelProviderOfflinePeriodRequest, void>(
      '/deliveryProviderPortal/cancelOfflinePeriod',
      request,
    );
  };

  public feedbackShouldGetFeedback = async (
    request: ShouldGetFeedbackPortalRequest,
  ): Promise<ShouldGetFeedbackPortalResponse> => {
    return this.postJSON<ShouldGetFeedbackPortalRequest, ShouldGetFeedbackPortalResponse>(
      '/deliveryProviderPortal/feedback/shouldGetFeedback',
      request,
    );
  };

  public feedbackSetFeedback = async (request: SetFeedbackPortalRequest): Promise<void> => {
    return this.postJSON<SetFeedbackPortalRequest, void>(
      '/deliveryProviderPortal/feedback/setFeedback',
      request,
    );
  };

  public feedbackSkipFeedback = async (request: SkipFeedbackPortalRequest): Promise<void> => {
    return this.postJSON<SkipFeedbackPortalRequest, void>(
      '/deliveryProviderPortal/feedback/skipFeedback',
      request,
    );
  };
}
